/**
 * Socket Controller - Stale-Closure-Proof Global Event Bindings
 * 
 * Binds Socket.IO events directly to Zustand store actions using getState()
 * to avoid React closure staleness. This runs OUTSIDE the React tree.
 * 
 * MANDATE: Do NOT bind socket listeners inside React components using useEffect.
 * Instead, bind globally here or within Zustand store actions.
 */

import { 
  getRawSocket, 
  typedOn, 
  typedOff
} from './gameSocket';
import type {
  MatchStartedPayload,
  FogViewPayload,
  VisibleMovePayload,
  CaptureEventPayload,
  CheckWarningPayload,
  EndgameResultPayload,
  QueueJoinedPayload,
  QueueLeftPayload,
  OpponentDisconnectedPayload,
  OpponentReconnectedPayload,
  ErrorPayload,
  ServerRebootingPayload,
  MatchFoundPayload,
} from '../../types';
import { 
  useGameStore, 
  useMatchmakingStore,
  useSettingsStore,
  type OpponentConnectionState 
} from '../../stores';
import { audioController, type GameOverOutcome } from '../../utils/audioController';

/**
 * Controller bound flag - prevents double-binding in React 18 StrictMode
 */
let isControllerBound = false;

/** Settings store subscription handle — cleaned up on unbind. */
let settingsUnsub: (() => void) | null = null;

/**
 * Bind all server-to-client events to Zustand store actions
 * Uses store.getState() to always access fresh state (no stale closures)
 */
export function bindSocketController(): void {
  if (isControllerBound) {
    console.warn('[SocketController] Already bound, skipping');
    return;
  }
  
  const socket = getRawSocket();
  if (!socket) {
    console.warn('[SocketController] No socket available, will bind on connect');
    // The controller will be bound when socket connects
    return;
  }
  
  console.log('[SocketController] Binding socket events to Zustand stores');
  
  // ============================================================
  // MATCH LIFECYCLE
  // ============================================================
  
  // match:started - Initial match setup after both players join
  typedOn('match:started', (payload: MatchStartedPayload) => {
    const { color, status, timer_seconds_left, your_setup_accepted, is_vs_ai, opponent_piece_skin_id } = payload;
    
    useGameStore.getState().setViewerColor(color);
    useGameStore.getState().setMatchStatus(status);
    useGameStore.getState().setTimer(Math.floor(Date.now() / 1000), timer_seconds_left);
    useGameStore.getState().setSetupAccepted(your_setup_accepted);
    useGameStore.getState().setIsVsAi(is_vs_ai ?? false);
    useGameStore.getState().setOpponentPieceSkinId(opponent_piece_skin_id ?? null);
    
    console.log('[SocketController] match:started', { color, status, timer: timer_seconds_left });
  });
  
  // game:state - Full FogView update (sent on join, after each move, on reconnect)
  typedOn('game:state', (payload: FogViewPayload) => {
    useGameStore.getState().setFogView(payload as any);
    useGameStore.getState().setInCheck(payload.in_check_for_viewer);
    useGameStore.getState().setTimer(Math.floor(Date.now() / 1000), payload.turn === useGameStore.getState().viewerColor ? 60 : 60); // Timer handled by server snapshot
    
    console.log('[SocketController] game:state', { 
      turn: payload.turn, 
      ownPieces: payload.own_pieces.length,
      opponentSquares: payload.opponent_squares.length,
      graveyard: payload.graveyard.length,
      inCheck: payload.in_check_for_viewer,
    });
  });
  
  // move:applied - Geometry-only move confirmation (both players)
  typedOn('move:applied', (payload: VisibleMovePayload) => {
    useGameStore.getState().setLastMove(payload as any);
    useGameStore.getState().setSubmittingMove(false); // Unlock move submission
    audioController.playMove();

    console.log('[SocketController] move:applied', { 
      from: payload.from_square, 
      to: payload.to_square, 
      mover: payload.mover_color,
      isCapture: payload.is_capture,
      isPromotion: payload.is_promotion,
    });
  });
  
  // piece:captured - Full capture details revealed to both players
  typedOn('piece:captured', (payload: CaptureEventPayload) => {
    useGameStore.getState().setLastCapture(payload as any);
    audioController.playCapture();

    console.log('[SocketController] piece:captured', { 
      captured: payload.captured_type, 
      by: payload.capturing_type,
      from: payload.capturing_from_square,
      to: payload.capturing_to_square,
    });
  });
  
  // check:warning - Check warning to threatened player only
  typedOn('check:warning', (payload: CheckWarningPayload) => {
    const { color } = payload;
    const viewerColor = useGameStore.getState().viewerColor;
    
    // Only set inCheck if it's our king in check
    if (viewerColor === color) {
      useGameStore.getState().setInCheck(true);
      audioController.playCheck();
    }

    console.log('[SocketController] check:warning', { color, viewerColor });
  });
  
  // game:over - Endgame with full truth revealed
  typedOn('game:over', (payload: EndgameResultPayload) => {
    useGameStore.getState().setMatchStatus('FINISHED');
    useGameStore.getState().setEndgameResult(payload as any);
    useGameStore.getState().setTimerPaused(true);
    useGameStore.getState().setSubmittingMove(false);

    // At game:over the full truth is revealed — derive the viewer's outcome
    // for the win/lose/draw chime.
    const viewerColor = useGameStore.getState().viewerColor;
    let outcome: GameOverOutcome;
    if (payload.winner_color && viewerColor) {
      outcome = payload.winner_color === viewerColor ? 'win' : 'lose';
    } else {
      outcome = 'draw';
    }
    audioController.playGameOver(outcome);

    console.log('[SocketController] game:over', { 
      type: payload.type, 
      winner: payload.winner_color,
      plyCount: payload.ply_count,
    });
  });
  
  // ============================================================
  // MATCHMAKING
  // ============================================================
  
  // queue:joined - Confirmed in queue
  typedOn('queue:joined', (payload: QueueJoinedPayload) => {
    useMatchmakingStore.getState().setQueued(true, payload.stage, payload.message);
    console.log('[SocketController] queue:joined', payload);
  });
  
  // queue:left - Left queue (cancelled or matched)
  typedOn('queue:left', (payload: QueueLeftPayload) => {
    useMatchmakingStore.getState().setQueueLeft(payload.message);
    console.log('[SocketController] queue:left', payload);
  });
  
  // match:found - Match found (from matchmaking or room)
  typedOn('match:found', (payload: MatchFoundPayload) => {
    useMatchmakingStore.getState().setMatchFound(payload);
    useMatchmakingStore.getState().clearAiFallback();
    console.log('[SocketController] match:found', { 
      matchId: payload.match_id, 
      opponent: payload.opponent_username,
      isVsAi: payload.is_vs_ai,
    });
  });
  
  // ============================================================
  // CONNECTION STATUS
  // ============================================================
  
  // opponent:disconnected - Opponent lost connection, grace period starts
  typedOn('opponent:disconnected', (payload: OpponentDisconnectedPayload) => {
    const { player, grace_until } = payload;
    const connectionState: Partial<OpponentConnectionState> = {
      isDisconnected: true,
      player,
      graceUntil: grace_until,
    };
    
    useGameStore.getState().setOpponentConnection(connectionState);
    
    console.log('[SocketController] opponent:disconnected', { 
      player, 
      graceUntil: grace_until,
      secondsLeft: grace_until - Math.floor(Date.now() / 1000),
    });
  });
  
  // opponent:reconnected - Opponent rejoined
  typedOn('opponent:reconnected', (payload: OpponentReconnectedPayload) => {
    useGameStore.getState().setOpponentConnection({
      isDisconnected: false,
      player: null,
      graceUntil: null,
    });
    
    console.log('[SocketController] opponent:reconnected', { player: payload.player });
  });
  
  // ============================================================
  // ERROR HANDLING (all error:* events)
  // ============================================================
  
  const errorEvents = [
    'error:server_rebooting',
    'error:invalid_payload',
    'error:unknown_match',
    'error:not_in_match',
    'error:not_authenticated',
    'error:wrong_phase',
    'error:feature_disabled',
    'error:service_unavailable',
    'error:gate_locked',
    'error:already_queued',
    'error:not_joined',
    'error:no_active_match',
    'error:lock_busy',
    'error:corrupted_state',
    'error:not_your_turn',
    'error:internal',
    'error:not_found',
    'error:not_room_owner',
    'error:room_not_ready',
    'error:not_room_member',
    'error:cannot_spectate_own_match',
    'error:spectate_not_available',
  ] as const;
  
  errorEvents.forEach((event) => {
    typedOn(event, (payload: ErrorPayload) => {
      console.error(`[SocketController] ${event}`, payload.reason, payload.details);
      
      // Handle specific errors with store actions
      switch (event) {
        case 'error:not_your_turn':
          useGameStore.getState().setSubmittingMove(false);
          break;
        case 'error:gate_locked':
          // Matchmaking store handles gate locking via UI
          break;
        case 'error:not_in_match':
        case 'error:no_active_match':
          useGameStore.getState().resetMatch();
          break;
        case 'error:wrong_phase':
          useGameStore.getState().setSubmittingMove(false);
          break;
      }
    });
  });
  
  // server:rebooting - Graceful shutdown notice
  typedOn('server:rebooting', (payload: ServerRebootingPayload) => {
    console.warn('[SocketController] server:rebooting', payload);
    // Could trigger a global notification here
  });
  
  // ============================================================
  // ZOMBIE LOCK PROTECTION
  // ============================================================
  // Native Socket.IO disconnect event - releases move submission lock
  // if connection drops after move:submit was sent but before move:applied received.
  // This prevents permanent UI freeze on silent reconnection.
  const rawSocket = getRawSocket();
  if (rawSocket) {
    rawSocket.on('disconnect', (reason: string) => {
      console.warn('[SocketController] Native disconnect detected, releasing submission lock', { reason });
      useGameStore.getState().setSubmittingMove(false);
    });
  }
  
  isControllerBound = true;
  console.log('[SocketController] All events bound successfully');

  // Settings store subscription — keep audioController in sync with soundEnabled.
  // Runs outside React so subscribe() is safe; cleaned up in unbindSocketController.
  if (!settingsUnsub) {
    settingsUnsub = useSettingsStore.subscribe(
      (state, prevState) => {
        if (state.soundEnabled !== prevState.soundEnabled) {
          audioController.setSoundEnabled(state.soundEnabled);
        }
      }
    );
    // Initialize with current value.
    audioController.setSoundEnabled(useSettingsStore.getState().soundEnabled);
  }
}

/**
 * Unbind all controller events (cleanup on logout/app unmount)
 */
export function unbindSocketController(): void {
  if (!isControllerBound) {
    return;
  }
  
  console.log('[SocketController] Unbinding all events');
  
  // Unbind all known events
  const eventsToUnbind = [
    'match:started',
    'game:state',
    'move:applied',
    'piece:captured',
    'check:warning',
    'game:over',
    'queue:joined',
    'queue:left',
    'match:found',
    'opponent:disconnected',
    'opponent:reconnected',
    'server:rebooting',
    ...[
      'error:server_rebooting',
      'error:invalid_payload',
      'error:unknown_match',
      'error:not_in_match',
      'error:not_authenticated',
      'error:wrong_phase',
      'error:feature_disabled',
      'error:service_unavailable',
      'error:gate_locked',
      'error:already_queued',
      'error:not_joined',
      'error:no_active_match',
      'error:lock_busy',
      'error:corrupted_state',
      'error:not_your_turn',
      'error:internal',
      'error:not_found',
      'error:not_room_owner',
      'error:room_not_ready',
      'error:not_room_member',
      'error:cannot_spectate_own_match',
      'error:spectate_not_available',
    ],
  ] as const;
  
  eventsToUnbind.forEach((event) => {
    typedOff(event);
  });
  
  // Remove zombie lock protection listener
  const socket = getRawSocket();
  if (socket) {
    socket.off('disconnect');
  }
  
  // Clean up settings subscription
  if (settingsUnsub) {
    settingsUnsub();
    settingsUnsub = null;
  }

  isControllerBound = false;
}

/**
 * Check if controller is currently bound
 */
export function isSocketControllerBound(): boolean {
  return isControllerBound;
}

/**
 * Get raw socket for advanced use cases
 */
export function getControllerSocket() {
  return getRawSocket();
}

/**
 * Auto-bind controller when socket connects
 * Call this once during app initialization (e.g., in RootLayout)
 */
export function autoBindOnConnect(): () => void {
  const socket = getRawSocket();
  
  if (!socket) {
    console.warn('[SocketController] No socket for auto-bind');
    return () => {};
  }
  
  const onConnect = () => {
    console.log('[SocketController] Auto-binding on connect');
    bindSocketController();
  };
  
  const onDisconnect = () => {
    console.log('[SocketController] Socket disconnected, controller stays bound for reconnect');
    // Don't unbind - we want to catch events on reconnect
  };
  
  socket.on('connect', onConnect);
  socket.on('disconnect', onDisconnect);
  
  // If already connected, bind immediately
  if (socket.connected) {
    bindSocketController();
  }
  
  // Return cleanup function
  return () => {
    socket.off('connect', onConnect);
    socket.off('disconnect', onDisconnect);
    unbindSocketController();
  };
}